import React from 'react';
import Icon from '../../../components/AppIcon';
import { formatDZ } from '../../../utils/currency';
import Image from '../../../components/AppImage';

const OrderSummary = ({ isSticky = false }) => {
  const cartItems = [
    {
      id: 1,
      name: "iPhone 15 Pro Max",
      variant: "256GB, Natural Titanium",
      price: 1199.00,
      quantity: 1,
      image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&h=400&fit=crop"
    },
    {
      id: 2,
      name: "AirPods Pro (2nd Gen)",
      variant: "USB-C, White",
      price: 249.00,
      quantity: 2,
      image: "https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=400&h=400&fit=crop"
    },
    {
      id: 3,
      name: "MagSafe Charger",
      variant: "15W Wireless Charging",
      price: 39.00,
      quantity: 1,
      image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=400&fit=crop"
    }
  ];

  const subtotal = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
  const shipping = 15.99;
  const tax = subtotal * 0.08;
  const discount = 50.00;
  const total = subtotal + shipping + tax - discount;

  const promoCode = "TECH20";

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${isSticky ? 'sticky top-24' : ''}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center">
          <Icon name="ShoppingBag" size={20} className="text-secondary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Order Summary</h2>
          <p className="text-sm text-muted-foreground">{cartItems?.length} items in your cart</p>
        </div>
      </div>
      {/* Cart Items */}
      <div className="space-y-4 mb-6">
        {cartItems?.map((item) => (
          <div key={item?.id} className="flex items-center space-x-4 p-3 bg-background/50 rounded-lg">
            <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
              <Image
                src={item?.image}
                alt={item?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground truncate">{item?.name}</h3>
              <p className="text-sm text-muted-foreground">{item?.variant}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm text-muted-foreground">Qty: {item?.quantity}</span>
                <span className="font-semibold text-foreground">{formatDZ(item?.price * item?.quantity)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Promo Code */}
      <div className="mb-6 p-3 bg-success/10 border border-success/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon name="Tag" size={16} className="text-success" />
          <span className="text-sm font-medium text-success">Promo Code Applied</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-sm text-muted-foreground">{promoCode}</span>
          <span className="text-sm font-semibold text-success">-${discount?.toFixed(2)}</span>
        </div>
      </div>
      {/* Order Totals */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground">{formatDZ(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Shipping</span>
          <span className="text-foreground">{formatDZ(shipping)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Tax</span>
          <span className="text-foreground">{formatDZ(tax)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-success">Discount ({promoCode})</span>
          <span className="text-success">-{formatDZ(discount)}</span>
        </div>
        <div className="border-t border-border pt-3">
          <div className="flex justify-between">
            <span className="text-lg font-semibold text-foreground">Total</span>
            <span className="text-lg font-bold text-primary">{formatDZ(total)}</span>
          </div>
        </div>
      </div>
      {/* Estimated Delivery */}
      <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Truck" size={16} className="text-primary" />
          <span className="text-sm font-medium text-primary">Estimated Delivery</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Free shipping • Arrives by <span className="font-medium text-foreground">Friday, Sep 29</span>
        </p>
      </div>
      {/* Trust Badges */}
      <div className="flex items-center justify-center space-x-4 mt-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="trust-seal p-1.5 bg-success/10 border border-success/30 rounded">
            <Icon name="Shield" size={14} className="text-success" />
          </div>
          <span className="text-xs text-muted-foreground">SSL Secure</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="trust-seal p-1.5 bg-primary/10 border border-primary/30 rounded">
            <Icon name="Lock" size={14} className="text-primary" />
          </div>
          <span className="text-xs text-muted-foreground">256-bit Encryption</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="trust-seal p-1.5 bg-warning/10 border border-warning/30 rounded">
            <Icon name="RefreshCw" size={14} className="text-warning" />
          </div>
          <span className="text-xs text-muted-foreground">30-day Returns</span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;